/**
 * Боковая панель справа: затемнение и колонка `min(520px, 100vw)` со своей прокруткой.
 * Закрывается крестиком, Esc и кликом по затемнению; при открытии фокус на крестике.
 * Состояние «открыта» живёт у вызывающего экрана, как у `Dialog`.
 */
import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

import { btnGhost } from "./classes";

export function SideSheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Свежий `onClose` для Esc: инлайн-колбэк родителя меняется на каждом рендере, фокус из-за него не трогаем.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        className="flex h-full w-[min(520px,100vw)] flex-col overflow-y-auto border-l border-border bg-bg text-fg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-bg px-4 py-2.5">
          <div className="min-w-0 flex-1 text-[14px] font-medium text-fg">{title}</div>
          <button ref={closeRef} type="button" className={btnGhost} onClick={onClose} aria-label="Закрыть">
            <X size={14} />
          </button>
        </div>
        {children}
      </aside>
    </div>
  );
}
